<template>
  <div class="p-6 w-full h-full flex flex-col bg-white rounded-2xl">
    <!-- title and search bar -->
    <div class="flex flex-row items-center justify-between">
      <p class="text-3xl font-bold">Admins</p>
      <div class="flex flex-row items-center justify-between space-x-4">
        <div class="relative group">
          <input
            type="text"
            v-model="searchText"
            class="bg-light-gray px-4 py-3 rounded-full font-sans"
            placeholder="Search for admin"
            v-on:keyup.enter="handleSearch"
          />
          <MagnifyingGlassIcon
            class="absolute bottom-3 right-3 w-6 h-6 text-gray"
          />
        </div>
        <div class="relative cursor-pointer">
          <div>
            <listbox v-model="filterBy">
              <ListboxButton>
                <div class="flex flex-row space-x-2 items-center">
                  <p class="text-gray">Filter by:</p>
                  <p class="text-blue">{{ filterBy.role }}</p>
                  <p v-if="!filterBy" class="text-gray">None</p>
                  <ChevronDownIcon class="w-4 h-4 text-gray" />
                </div>
              </ListboxButton>
              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-out"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
              >
                <ListboxOptions
                  class="
                    absolute
                    py-2
                    w-full
                    bg-white
                    rounded-lg
                    shadow-md
                    border border-light-gray
                  "
                >
                  <ListboxOption
                    v-for="option in filterOptions"
                    :key="option.key"
                    :value="option"
                    @click="handleSearch"
                    class="hover:bg-light-gray px-4 py-2 font-sans font-md"
                  >
                    {{ option.role }}
                  </ListboxOption>
                </ListboxOptions>
              </transition>
            </listbox>
          </div>
        </div>
        <BaseRoundedButton @click="handleSlideOver">
          <p class="font-sans">Create admin</p>
        </BaseRoundedButton>
      </div>
    </div>
    <!-- body -->
    <div>
      <table class="table-auto w-full font-light">
        <thead class="border-b-2 border-light-gray">
          <tr class="text-left text-gray text-md">
            <th class="py-2 font-normal">Name</th>
            <th class="py-2 font-normal">Email</th>
            <th class="py-2 font-normal text-center">Role</th>
            <th class="py-2 font-normal">Create at</th>
            <th class="py-2 font-normal">Update at</th>
            <th class="py-2 font-normal text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="admin in admins" :key="admin.id" class="text-lg">
            <td class="py-3">{{ admin.name }}</td>
            <td>{{ admin.email }}</td>
            <td>
              <div class="flex justify-center select-none">
                <div
                  class="px-4 py-1 bg-light-blue text-blue rounded-full w-min"
                >
                  {{ admin.role.name }}
                </div>
              </div>
            </td>
            <td>
              {{ moment(admin.created_at).format("MMM DD, YYYY h:mm A") }}
            </td>
            <td>
              {{ moment(admin.updated_at).format("MMM DD, YYYY h:mm A") }}
            </td>
            <td>
              <div class="flex justify-center relative">
                <Menu>
                  <MenuButton
                    ><EllipsisVerticalIcon
                      class="w-8 h-8 text-black hover:text-blue cursor-pointer"
                  /></MenuButton>
                  <MenuItems
                    class="
                      p-1
                      space-y-1
                      absolute
                      top-8
                      right-8
                      z-50
                      bg-white
                      border border-light-gray
                      shadow-md
                      rounded-lg
                      w-12/12
                    "
                  >
                    <MenuItem
                      @click="handleEdit(admin)"
                      class="hover:bg-light-blue m-1 rounded-md cursor-pointer"
                    >
                      <div
                        class="flex flex-row items-center px-4 py-2 min-w-min"
                      >
                        <PencilIcon class="text-blue w-5 h-5 mr-4" />
                        <p class="text-blue">Edit</p>
                      </div>
                    </MenuItem>
                    <MenuItem
                      @click="handleReset(admin)"
                      class="
                        hover:bg-light-blue
                        m-1
                        rounded-md
                        cursor-pointer
                        w-max
                      "
                    >
                      <div class="flex flex-row items-center px-4 py-2">
                        <KeyIcon class="text-blue w-5 h-5 mr-4" />
                        <p class="text-blue">Reset Password</p>
                      </div>
                    </MenuItem>
                    <MenuItem
                      @click="handleDelete(admin.id)"
                      class="hover:bg-light-blue m-1 rounded-md cursor-pointer"
                    >
                      <div
                        class="flex flex-row items-center px-4 py-2 min-w-min"
                      >
                        <TrashIcon class="text-red w-5 h-5 mr-4" />
                        <p class="text-red">Delete</p>
                      </div>
                    </MenuItem>
                  </MenuItems>
                </Menu>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <!-- create new admin slide over side bar -->
      <SideBar v-show="showSlideOver" @close-modal="handleSlideOver">
        <div class="w-full min-h-screen flex flex-col content-between">
          <h1 class="text-blue text-2xl">Create New Admin</h1>
          <div class="flex flex-col space-y-4 py-8">
            <base-text-input
              v-model="name"
              name="name"
              isAutoComplete="off"
              label="Name"
            />
            <base-text-input v-model="email" name="email" label="Email" />
            <base-text-input
              v-model="pass"
              name="pass"
              isAutoComplete="off"
              label="Password"
            />
            <div class="relative">
              <listbox v-model="role">
                <ListboxButton class="w-full">
                  <div>
                    <p class="text-left text-gray mb-2">Role</p>
                    <div
                      class="
                        flex
                        items-center
                        justify-between
                        w-full
                        px-2
                        py-3
                        rounded-xl
                        border border-dark-gray
                      "
                    >
                      <p v-if="!role" class="text-left text-gray">
                        Select role
                      </p>
                      <p v-else class="text-blue">{{ role }}</p>
                      <ChevronDownIcon class="w-4 h-4 text-gray" />
                    </div>
                  </div>
                </ListboxButton>
                <transition
                  enter-active-class="transition duration-100 ease-out"
                  enter-from-class="transform scale-95 opacity-0"
                  enter-to-class="transform scale-100 opacity-100"
                  leave-active-class="transition duration-75 ease-out"
                  leave-from-class="transform scale-100 opacity-100"
                  leave-to-class="transform scale-95 opacity-0"
                >
                  <ListboxOptions
                    class="
                      absolute
                      py-2
                      w-full
                      bg-white
                      rounded-lg
                      shadow-md
                      border border-light-gray
                    "
                  >
                    <ListboxOption
                      v-for="role in roleList"
                      :key="role.id"
                      :value="role.name"
                      class="hover:bg-light-gray px-4 py-2 font-sans font-md"
                    >
                      {{ role.name }}
                    </ListboxOption>
                  </ListboxOptions>
                </transition>
              </listbox>
            </div>
            <div
              v-if="this.errorMsg"
              class="w-full bg-light-red text-left text-red p-4 rounded-md"
            >
              <p>{{ this.errorMsg }}</p>
            </div>
          </div>
          <!-- action button -->
          <div
            class="
              flex flex-row
              items-center
              justify-between
              space-x-4
              mt-auto
              mb-12
            "
          >
            <BaseRoundedButton
              @click="handleSlideOver"
              class="bg-dark-gray w-2/6"
              >Cancel</BaseRoundedButton
            >
            <BaseRoundedButton @click="handleCreate" class="w-4/6"
              >Create</BaseRoundedButton
            >
          </div>
        </div>
      </SideBar>
      <!-- Update admin slide over side bar -->
      <SideBar v-show="updateSlideOver" @close-modal="handleUpdateSlideOver">
        <div class="w-full min-h-screen flex flex-col content-between">
          <h1 class="text-blue text-2xl">Update Admin Information</h1>
          <div class="flex flex-col space-y-4 py-8">
            <base-text-input
              v-model="updatedName"
              name="name"
              isAutoComplete="off"
              label="Name"
            />
            <base-text-input
              v-model="updatedEmail"
              name="email"
              label="Email"
            />
            <div class="relative">
              <listbox v-model="updatedRole">
                <ListboxButton class="w-full">
                  <div>
                    <p class="text-left text-gray mb-2">Role</p>
                    <div
                      class="
                        flex
                        items-center
                        justify-between
                        w-full
                        px-2
                        py-3
                        rounded-xl
                        border border-dark-gray
                      "
                    >
                      <p v-if="!updatedRole" class="text-left text-gray">
                        Select role
                      </p>
                      <p v-else class="text-blue">{{ updatedRole }}</p>
                      <ChevronDownIcon class="w-4 h-4 text-gray" />
                    </div>
                  </div>
                </ListboxButton>
                <transition
                  enter-active-class="transition duration-100 ease-out"
                  enter-from-class="transform scale-95 opacity-0"
                  enter-to-class="transform scale-100 opacity-100"
                  leave-active-class="transition duration-75 ease-out"
                  leave-from-class="transform scale-100 opacity-100"
                  leave-to-class="transform scale-95 opacity-0"
                >
                  <ListboxOptions
                    class="
                      absolute
                      py-2
                      w-full
                      bg-white
                      rounded-lg
                      shadow-md
                      border border-light-gray
                    "
                  >
                    <ListboxOption
                      v-for="role in roleList"
                      :key="role.id"
                      :value="role.name"
                      class="hover:bg-light-gray px-4 py-2 font-sans font-md"
                    >
                      {{ role.name }}
                    </ListboxOption>
                  </ListboxOptions>
                </transition>
              </listbox>
            </div>
          </div>
          <!-- action button -->
          <div
            class="
              flex flex-row
              items-center
              justify-between
              space-x-4
              mt-auto
              mb-12
            "
          >
            <BaseRoundedButton
              @click="handleUpdateSlideOver"
              class="bg-dark-gray w-2/6"
              >Cancel</BaseRoundedButton
            >
            <BaseRoundedButton @click="handleUpdate" class="w-4/6"
              >Update</BaseRoundedButton
            >
          </div>
        </div>
      </SideBar>
      <!-- Reset Pass slide over side bar -->
      <SideBar v-show="resetSlideOver" @close-modal="handleResetSlideOver">
        <div class="w-full min-h-screen flex flex-col content-between">
          <h1 class="text-blue text-2xl">Reset Password</h1>
          <div class="flex flex-col space-y-4 py-8">
            <base-text-input
              v-model="updatedPass"
              name="pass"
              isAutoComplete="off"
              label="New Password"
            />
          </div>
          <!-- action button -->
          <div
            class="
              flex flex-row
              items-center
              justify-between
              space-x-4
              mt-auto
              mb-12
            "
          >
            <BaseRoundedButton
              @click="handleResetSlideOver"
              class="bg-dark-gray w-2/6"
              >Cancel</BaseRoundedButton
            >
            <BaseRoundedButton @click="handleResetPass" class="w-4/6"
              >Reset Password</BaseRoundedButton
            >
          </div>
        </div>
      </SideBar>
    </div>
    <div class="flex mt-auto items-center justify-between">
      <div class="relative">
        <listbox v-model="count">
          <ListboxButton class="w-full">
            <div class="flex items-center space-x-2">
              <p class="text-left text-gray mb-2">Rows</p>
              <div
                class="
                  flex
                  items-center
                  justify-between
                  w-20
                  px-2
                  py-3
                  rounded-xl
                  border border-dark-gray
                "
              >
                <p class="text-left text-gray">
                  {{ count }}
                </p>
                <ChevronDownIcon class="w-4 h-4 text-gray" />
              </div>
            </div>
          </ListboxButton>
          <transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-75 ease-out"
            leave-from-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-95 opacity-0"
          >
            <ListboxOptions
              class="
                absolute
                bottom-14
                py-2
                w-full
                bg-white
                rounded-lg
                shadow-md
                border border-light-gray
              "
            >
              <ListboxOption
                v-for="(option, index) in countOptions"
                :key="index"
                :value="option"
                @click="handleSearch"
                class="hover:bg-light-gray px-4 py-2 font-sans font-md"
              >
                {{ option }}
              </ListboxOption>
            </ListboxOptions>
          </transition>
        </listbox>
      </div>
      <BasePagination
        @navigateToPage="getAdminList($event)"
        :pageCount="adminMetaLink.last_page"
      />
    </div>
  </div>
</template>

<script>
import {
  MagnifyingGlassIcon,
  ChevronDownIcon,
  EllipsisVerticalIcon,
} from "@heroicons/vue/24/solid";
import { PencilIcon, TrashIcon, KeyIcon } from "@heroicons/vue/24/outline";
import BaseRoundedButton from "../components/BaseRoundedButton.vue";
import moment from "moment";
import { useToast } from "vue-toastification";
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
} from "@headlessui/vue";
import SideBar from "../components/SideBar.vue";
import BaseTextInput from "../components/BaseTextInput.vue";
import userService from "../Services/user.service";
import BasePagination from "../components/BasePagination.vue";
export default {
  name: "Admins",
  components: {
    MagnifyingGlassIcon,
    BaseRoundedButton,
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
    ChevronDownIcon,
    EllipsisVerticalIcon,
    Menu,
    MenuButton,
    MenuItems,
    MenuItem,
    PencilIcon,
    TrashIcon,
    SideBar,
    BaseTextInput,
    KeyIcon,
    BasePagination,
  },
  data() {
    return {
      searchText: "",
      filterBy: "",
      filterOptions: [
        {
          key: "",
          role: "None",
        },
        {
          key: "admin",
          role: "Admin",
        },
        {
          key: "editor",
          role: "Editor",
        },
      ],
      showSlideOver: false,
      updateSlideOver: false,
      resetSlideOver: false,
      name: "",
      email: "",
      pass: "",
      role: "",
      updatedName: "",
      updatedEmail: "",
      updatedRole: "",
      updatedPass: "",
      updateID: "",
      errorMsg: "",
      roleList: [],
      admins: [],
      adminMetaLink: [],
      count: "15",
      countOptions: ["5", "10", "15"],
      params: new URLSearchParams(),
    };
  },
  methods: {
    handleSlideOver() {
      this.showSlideOver = !this.showSlideOver;
    },
    handleUpdateSlideOver() {
      this.updateSlideOver = !this.updateSlideOver;
    },
    handleResetSlideOver() {
      this.resetSlideOver = !this.resetSlideOver;
    },
    handleReset(admin) {
      this.handleResetSlideOver();
      this.updateID = admin.id;
    },
    handleEdit(admin) {
      this.handleUpdateSlideOver();
      this.updatedName = admin.name;
      this.updatedEmail = admin.email;
      this.updatedRole = admin.role.name;
      this.updateID = admin.id;
    },
    handleUpdate() {
      this.$Progress.start();
      let formData = new FormData();
      formData.append("name", this.updatedName);
      formData.append("email", this.updatedEmail);
      formData.append("role", this.updatedRole);
      userService
        .storeAdminUpdate(this.updateID, formData)
        .then(() => {
          this.$Progress.finish();
          this.updatedName = "";
          this.updatedEmail = "";
          this.updatedRole = "";
          this.updateID = "";
          this.handleUpdateSlideOver();
          this.toast.success(
            "Admin information has been updated successfully."
          );
          this.getAdminList();
        })
        .catch(() => {
          this.$Progress.fail();
          this.toast.error("Something went wrong! Unable to update admin!");
        });
    },
    handleResetPass() {
      this.$Progress.start();
      let formData = new FormData();
      formData.append("password", this.updatedPass);

      userService
        .storeAdminUpdatePass(this.updateID, formData)
        .then(() => {
          this.$Progress.finish();
          this.updateID = "";
          this.updatedPass = "";
          this.toast.success("Reset password successfully!");
          this.handleResetSlideOver();
        })
        .catch((error) => {
          this.$Progress.fail();
          this.toast.error("Something went wrong! Unable to reset password!");
          console.log(error);
        });
    },
    handleDelete(id) {
      this.$Progress.start();
      userService
        .delAdmin(id)
        .then(() => {
          this.$Progress.finish();
          this.toast.success("Admin has been deleted successfully.");
          this.getAdminList();
        })
        .catch((error) => {
          this.$Progress.fail();
          this.toast.error(
            "Something went wrong! Unable to delete this admin!"
          );
          console.log(error);
        });
    },
    handleCreate() {
      this.$Progress.start();
      let formData = new FormData();
      formData.append("name", this.name);
      formData.append("email", this.email);
      formData.append("password", this.pass);
      formData.append("role", this.role);

      userService
        .storeAdmin(formData)
        .then(() => {
          this.showSlideOver = !this.showSlideOver;
          // clear binded data
          this.name = "";
          this.email = "";
          this.pass = "";
          this.role = "";
          this.$Progress.finish();
          this.toast.success("New admin has been created successfully.");
          this.errorMsg = "";
          this.getAdminList();
        })
        .catch((error) => {
          this.$Progress.fail();
          this.errorMsg =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            error.message ||
            error.toString();
        });
    },
    getAdminList() {
      this.params.set("count", this.count);
      userService.getAdmins(this.params).then((response) => {
        this.admins = response.data.data;
        this.adminMetaLink = response.data.meta;
      });
    },
    handleSearch() {
      this.$Progress.start();
      this.params.set("count", this.count);
      if (this.searchText) {
        this.params.set("search", this.searchText);
      } else {
        this.params.delete("search");
      }

      if (this.filterBy.key) {
        this.params.set("role", this.filterBy.key);
      } else {
        this.params.delete("role");
      }
      userService.getAdmins(this.params).then((response) => {
        this.admins = response.data.data;
        this.adminMetaLink = response.data.meta;
        this.$Progress.finish();
      });
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  created() {
    this.$Progress.start();
    this.moment = moment;
    userService.getRoleList().then((response) => {
      this.roleList = response.data.data;
    });
    this.getAdminList();
  },
  mounted() {
    this.$Progress.finish();
  },
};
</script>

<style scoped>
</style>
